import React from 'react';
import { shouldShowCount } from '../../services/tplSeverityStyles';
import { estimateLossFinancialImpact } from '../../services/dataflowInsightEngine';

export interface LossRankItem {
  rank: number;
  title: string;
  count: number;
  isCritical?: boolean;
}

interface DataflowLossRankingProps {
  items: LossRankItem[];
  totalLosses: number;
  criticalCount?: number;
}

/** TOP perdas — quais rotinas destroem o resultado. */
export const DataflowLossRanking: React.FC<DataflowLossRankingProps> = ({
  items,
  totalLosses,
  criticalCount = 0,
}) => {
  const top5 = items.slice(0, 5);
  const financial = estimateLossFinancialImpact(totalLosses, criticalCount);

  if (top5.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-slate-200 px-3 py-6 text-center text-[11px] text-emerald-600 dark:border-slate-700 dark:text-emerald-400">
        Nenhuma perda registrada no período.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <section className="rounded-lg border border-slate-200 dark:border-slate-800">
        <header className="border-b border-slate-100 px-3 py-1.5 dark:border-slate-800">
          <h4 className="text-[10px] font-bold uppercase tracking-wide text-slate-600 dark:text-slate-300">
            Top 5 perdas
          </h4>
          <p className="text-[9px] text-slate-400">Quais rotinas estão destruindo o resultado?</p>
        </header>
        <ol className="divide-y divide-slate-100 dark:divide-slate-800">
          {top5.map((item) => (
            <li key={`${item.rank}-${item.title}`} className="flex items-center gap-2 px-3 py-1.5">
              <span className="w-4 shrink-0 text-[10px] font-bold tabular-nums text-slate-400">
                {item.rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11px] font-semibold text-slate-800 dark:text-slate-100">
                {item.title}
              </span>
              <span
                className={`shrink-0 text-[11px] font-bold tabular-nums ${item.isCritical ? 'text-red-600' : 'text-amber-600'}`}
              >
                {item.count}
              </span>
            </li>
          ))}
        </ol>
      </section>

      {shouldShowCount(financial) && (
        <section className="rounded-lg border border-amber-200/80 bg-amber-50/40 px-3 py-2 dark:border-amber-900/40 dark:bg-amber-950/15">
          <p className="text-[9px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-400">
            Impacto financeiro estimado
          </p>
          <p className="text-[18px] font-black tabular-nums text-amber-800 dark:text-amber-300">
            R$ {financial.toLocaleString('pt-BR')}
          </p>
          <p className="text-[9px] text-slate-400">Estimativa operacional — não contábil.</p>
        </section>
      )}
    </div>
  );
};

export default DataflowLossRanking;
